import { useCookieConsent } from "@/contexts/CookieContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { useState, useEffect } from "react";
import { Cookie, X } from "lucide-react";

const CookieConsent = () => {
  const {
    showBanner,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    showPreferencesDialog,
    preferences,
  } = useCookieConsent();

  const [tempPreferences, setTempPreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    if (preferences) {
      setTempPreferences(preferences);
    }
  }, [preferences]);

  if (!showBanner) return null;

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border shadow-lg">
        <div className="container mx-auto px-4 py-6 sm:px-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-6 h-6 text-primary mt-1 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  Acest site folosește cookie-uri
                </p>
                <p className="text-xs text-muted-foreground">
                  Folosim cookie-uri pentru a îmbunătăți experiența ta pe site. Poți accepta toate cookie-urile sau le poți personaliza.
                </p>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                onClick={openPreferences}
                className="flex-1 sm:flex-none"
              >
                Personalizează
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={rejectAll}
                className="flex-1 sm:flex-none"
              >
                Respinge
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="flex-1 sm:flex-none"
              >
                Acceptă toate
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={showPreferencesDialog} onOpenChange={closePreferences}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl">Preferințe Cookie-uri</DialogTitle>
            <DialogDescription className="font-lora">
              Gestionează preferințele tale de cookie-uri. Cookie-urile necesare sunt întotdeauna active.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Necessary Cookies */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="necessary" className="font-medium text-base">
                  Cookie-uri Necesare
                </Label>
                <Switch
                  id="necessary"
                  checked={true}
                  disabled
                  className="opacity-50"
                />
              </div>
              <p className="text-sm text-muted-foreground font-lora">
                Aceste cookie-uri sunt esențiale pentru funcționarea corectă a site-ului și nu pot fi dezactivate.
              </p>
            </div>

            {/* Functional Cookies */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="functional" className="font-medium text-base">
                  Cookie-uri Funcționale
                </Label>
                <Switch
                  id="functional"
                  checked={tempPreferences.functional}
                  onCheckedChange={(checked) =>
                    setTempPreferences({ ...tempPreferences, functional: checked })
                  }
                />
              </div>
              <p className="text-sm text-muted-foreground font-lora">
                Aceste cookie-uri îmbunătățesc funcționalitatea site-ului și oferă caracteristici personalizate.
              </p>
            </div>

            {/* Analytics Cookies */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="analytics" className="font-medium text-base">
                  Cookie-uri de Analiză
                </Label>
                <Switch
                  id="analytics"
                  checked={tempPreferences.analytics}
                  onCheckedChange={(checked) =>
                    setTempPreferences({ ...tempPreferences, analytics: checked })
                  }
                />
              </div>
              <p className="text-sm text-muted-foreground font-lora">
                Aceste cookie-uri ne ajută să înțelegem cum este utilizat site-ul pentru a îmbunătăți experiența utilizatorilor.
              </p>
            </div>

            {/* Marketing Cookies */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="marketing" className="font-medium text-base">
                  Cookie-uri de Marketing
                </Label>
                <Switch
                  id="marketing"
                  checked={tempPreferences.marketing}
                  onCheckedChange={(checked) =>
                    setTempPreferences({ ...tempPreferences, marketing: checked })
                  }
                />
              </div>
              <p className="text-sm text-muted-foreground font-lora">
                Aceste cookie-uri sunt utilizate pentru a afișa reclame relevante pentru interesele tale.
              </p>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={rejectAll}
              className="w-full sm:w-auto"
            >
              Respinge Toate
            </Button>
            <Button
              onClick={() => savePreferences(tempPreferences)}
              className="w-full sm:w-auto"
            >
              Salvează Preferințele
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
